import React, { useMemo, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import { DataGridPro, GridColDef, GridSelectionModel } from "@mui/x-data-grid-pro"
import { formatUtcToLocal } from "../../helper/utils"
import type { MatchedExistingOtrItem } from "./OTRImportDuplicateDialogs"
import type { MatchedRemovedItem } from "./OTRRemovedQueueRestoreDialog"

export type CombinedDuplicateAction = "skip" | "all"
export type CombinedRemovedAction = "dont_restore" | "restore_selected" | "restore_all"

export type CombinedConflictChoice = {
  duplicateAction: CombinedDuplicateAction
  removedAction: CombinedRemovedAction
  selectedCustomerPolicyIds: number[]
}

type Props = {
  open: boolean
  duplicateMatches: MatchedExistingOtrItem[]
  removedMatches: MatchedRemovedItem[]
  totalImportCount: number
  onCancel: () => void
  onContinue: (choice: CombinedConflictChoice) => void
  loading?: boolean
}

const GRID_ROW_HEIGHT = 40
const GRID_HEADER_HEIGHT = 56

export function OTRImportCombinedConflictDialog({
  open,
  duplicateMatches,
  removedMatches,
  totalImportCount,
  onCancel,
  onContinue,
  loading = false,
}: Props) {
  const [duplicateAction, setDuplicateAction] = useState<CombinedDuplicateAction>("skip")
  const [removedAction, setRemovedAction] = useState<CombinedRemovedAction>("dont_restore")
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])

  const fmtLocal = (v: string | null | undefined) =>
    v != null && String(v).trim() !== "" ? formatUtcToLocal(v, "MM/DD/YYYY", true) : ""

  const duplicateRows = useMemo(
    () =>
      duplicateMatches.map((item, index) => ({
        id: `dup-${item.importRowIndex}-${item.newOpenTaxReportId}-${item.customerPolicyId}-${index}`,
        importRowIndex: item.importRowIndex ?? "",
        matchSource:
          item.matchSource === "OTR"
            ? "Pending OTR"
            : item.matchSource === "FilingOutstanding"
              ? "Outstanding filing"
              : item.matchSource === "FilingReadyToFile"
                ? "Ready to file"
                : item.matchSource === "FilingCompleted"
                  ? "Completed"
                  : item.matchSource ?? "",
        policyNumber: item.policyNumber ?? "",
        invoiceNumber: item.invoiceNumber ?? "",
        state: item.state ?? item.importStateCode ?? "",
        client: item.client ?? "",
        transactionType: item.transactionType ?? "",
        taxType: item.taxType ?? "",
        taxAmt: item.taxAmt != null && item.taxAmt !== "" ? String(item.taxAmt) : "",
        grossPremium: item.grossPremium ?? "",
        invDate: fmtLocal(item.invDate),
        effectiveDate: fmtLocal(item.effectiveDate),
        expirationDate: fmtLocal(item.expirationDate),
      })),
    [duplicateMatches]
  )

  const removedRows = useMemo(
    () =>
      removedMatches.map((item, index) => ({
        id: `rm-${item.customerPolicyId}-${index}`,
        _customerPolicyId: item.customerPolicyId,
        importRowIndex: item.importRowIndex ?? "",
        policyNumber: item.policyNumber ?? "",
        invoiceNumber: item.invoiceNumber ?? "",
        client: item.client ?? "",
        transactionType: item.transactionType ?? "",
        taxType: item.taxType ?? "",
        taxAmt: item.taxAmt ?? "",
        grossPremium: item.grossPremium ?? "",
        invDate: fmtLocal(item.invDate),
        effectiveDate: fmtLocal(item.effectiveDate),
        expirationDate: fmtLocal(item.expirationDate),
      })),
    [removedMatches]
  )

  const duplicateColumns: GridColDef[] = useMemo(
    () => [
      { field: "importRowIndex", headerName: "Import row", width: 90 },
      { field: "matchSource", headerName: "Match source", width: 150 },
      { field: "policyNumber", headerName: "Policy #", minWidth: 120, flex: 1 },
      { field: "state", headerName: "State", width: 72 },
      { field: "invoiceNumber", headerName: "Invoice #", width: 110 },
      { field: "client", headerName: "Client", minWidth: 140, flex: 1 },
      { field: "transactionType", headerName: "Transaction Type", width: 130 },
      { field: "taxType", headerName: "Tax Type", width: 120 },
      { field: "grossPremium", headerName: "Gross Premium", width: 110 },
      { field: "taxAmt", headerName: "Tax Amt", width: 90 },
      { field: "invDate", headerName: "Inv Date", width: 100 },
      { field: "effectiveDate", headerName: "Effective Date", width: 110 },
      { field: "expirationDate", headerName: "Expiration Date", width: 110 },
    ],
    []
  )

  const removedColumns: GridColDef[] = useMemo(
    () => [
      { field: "policyNumber", headerName: "Policy #", minWidth: 120, flex: 1 },
      { field: "invoiceNumber", headerName: "Invoice #", width: 110 },
      { field: "client", headerName: "Client", minWidth: 140, flex: 1 },
      { field: "transactionType", headerName: "Transaction Type", width: 130 },
      { field: "taxType", headerName: "Tax Type", width: 120 },
      { field: "taxAmt", headerName: "Tax Amt", width: 90 },
      { field: "grossPremium", headerName: "Gross Premium", width: 110 },
      { field: "invDate", headerName: "Inv Date", width: 100 },
      { field: "effectiveDate", headerName: "Effective Date", width: 110 },
      { field: "expirationDate", headerName: "Expiration Date", width: 110 },
    ],
    []
  )

  const selectedCustomerPolicyIds = useMemo(
    () =>
      removedRows
        .filter((r) => selectionModel.includes(r.id))
        .map((r) => r._customerPolicyId),
    [removedRows, selectionModel]
  )

  const disableContinue =
    loading || (removedAction === "restore_selected" && selectedCustomerPolicyIds.length === 0)

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>Resolve import conflicts</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", pb: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {duplicateMatches.length} duplicate match(es) and {removedMatches.length} removed-queue match(es) were found
          out of {totalImportCount} import row(s). Choose both actions below, then continue import once.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Duplicate matches
        </Typography>
        <Box sx={{ width: "100%" }}>
          <DataGridPro
            rows={duplicateRows}
            columns={duplicateColumns}
            autoHeight
            density="compact"
            disableSelectionOnClick
            rowHeight={GRID_ROW_HEIGHT}
            headerHeight={GRID_HEADER_HEIGHT}
            disableColumnMenu={false}
            loading={loading}
            hideFooter
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "flex-end" }}>
          Total Rows: {duplicateRows.length}
        </Typography>
        <FormControl sx={{ mt: 0.5 }}>
          <RadioGroup
            row
            value={duplicateAction}
            onChange={(_, v) => setDuplicateAction(v as CombinedDuplicateAction)}
          >
            <FormControlLabel value="skip" control={<Radio />} label="Skip matching rows" />
            <FormControlLabel value="all" control={<Radio />} label="Import all matching rows" />
          </RadioGroup>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Transactions in Removed Queue
        </Typography>
        <Box sx={{ width: "100%" }}>
          <DataGridPro
            rows={removedRows}
            columns={removedColumns}
            autoHeight
            density="compact"
            checkboxSelection
            disableSelectionOnClick
            rowHeight={GRID_ROW_HEIGHT}
            headerHeight={GRID_HEADER_HEIGHT}
            selectionModel={selectionModel}
            onSelectionModelChange={(model) => setSelectionModel(model)}
            disableColumnMenu={false}
            loading={loading}
            hideFooter
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "flex-end" }}>
          Total Rows: {removedRows.length}
        </Typography>
        <FormControl sx={{ mt: 0.5 }}>
          <RadioGroup row value={removedAction} onChange={(_, v) => setRemovedAction(v as CombinedRemovedAction)}>
            <FormControlLabel value="dont_restore" control={<Radio />} label="Don't restore" />
            <FormControlLabel value="restore_selected" control={<Radio />} label="Restore selected" />
            <FormControlLabel value="restore_all" control={<Radio />} label="Restore all" />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1.5,
          gap: 1,
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          zIndex: 1,
        }}
      >
        <Button onClick={onCancel} color="inherit" disabled={loading}>
          Cancel import
        </Button>
        <Button
          onClick={() =>
            onContinue({
              duplicateAction,
              removedAction,
              selectedCustomerPolicyIds,
            })
          }
          variant="contained"
          disabled={disableContinue}
        >
          Continue import
        </Button>
      </DialogActions>
    </Dialog>
  )
}
